import { z } from "zod";
import { readCombined } from "./combined";
import { formatDistance } from "./marks";
import { type Attempt, type HeightTries, attemptsSummary, rankByValue } from "./ranking";
import { ATTEMPT_RESULTS, type AttemptsResultDetail, RESULT_RANK_MAX, START_LIST_SLOTS_MAX } from "./results";
import { TIMING_LANE_PATTERN, type TimingProblem, type TimingProblemCode, type TimingSlot, timingLaneKey } from "./timing";
import { timingBenchSnapshot } from "./timing-bench";
import type { IrmCode } from "./vocab";

/**
 * Athletics FIELD events (jumps and throws) → the timing bench relay, the
 * pool and track timers' path (`timing-bench.ts`) for an ATTEMPTS meet:
 *
 *   field console ──relay (athleticsFieldToBenchSnapshot)──▶ PUT /timing/benches/:benchId   (Redis only)
 *   OVR ◀── GET /timing/benches/:benchId — shown on the open jump / throw
 *   OVR "Apply field results" ──▶ POST /meets/:id/timing/from-bench
 *        └─ normalizeFieldBench() ─▶ the SAME save statement as an operator's (writer TIMING)
 *
 * The snapshot is the device's card, BY BIB, tied to no meet: each athlete's
 * attempts (horizontal jumps, throws) or tries at each bar (vertical jumps),
 * with the device's status and place. Every decision about what is written —
 * which slot, the best mark, NM, the rank — is `normalizeFieldBench`'s.
 *
 * The only field source captured so far is the athletics SIMULATOR's
 * `GET /api/athletics/field/<discipline>` (TSR's Live Field board reads it
 * too); no real field console has been seen. Its shape is
 * `AthleticsFieldFeed` below, read by name, nothing guessed.
 */

export const FIELD_EVENT_KINDS = ["HORIZONTAL", "THROW", "VERTICAL"] as const;
export type FieldEventKind = (typeof FIELD_EVENT_KINDS)[number];

/** in the competition, stopped at the cut, out (three failures / series over), retired, did not start. */
export const FIELD_ATHLETE_STATUSES = ["IN", "CUT", "OUT", "RETIRED", "DNS"] as const;
export type FieldAthleteStatus = (typeof FIELD_ATHLETE_STATUSES)[number];

export const FIELD_BENCH_PHASES = ["READY", "RUNNING", "PAUSED", "FINISHED"] as const;
export type FieldBenchPhase = (typeof FIELD_BENCH_PHASES)[number];

/** A javelin past 100 m fits; nothing in athletics goes further. */
export const FIELD_MARK_MAX_MM = 150_000;
/** A pole vault bar, with room. */
export const FIELD_HEIGHT_MAX_MM = 7_000;
export const FIELD_ATTEMPTS_MAX = 12;
export const FIELD_HEIGHTS_MAX = 60;
/** The wind gauge's range, as the attempts grid takes it (m/s). */
export const FIELD_WIND_MAX_MPS = 20;

/** A vertical jump's tries at one height: `O`, `XO`, `XXO`, `X`, `XX`, `XXX`, `-`, `X-`, `XX-` (World Athletics TR 26). */
export const FIELD_TRIES_PATTERN = /^(X{0,2}O|X{0,2}-|X{1,3})$/;

const fieldAttempt = z
	.object({
		/** The round, 1-based. */
		seq: z.number().int().min(1).max(FIELD_ATTEMPTS_MAX),
		result: z.enum(ATTEMPT_RESULTS),
		/** VALID only, mm, floored to the centimetre. */
		mark_mm: z.number().int().min(0).max(FIELD_MARK_MAX_MM).nullable(),
		/** Horizontal jumps; one decimal. */
		wind_mps: z.number().min(-FIELD_WIND_MAX_MPS).max(FIELD_WIND_MAX_MPS).nullable(),
	})
	.strict();
export type FieldBenchAttempt = z.infer<typeof fieldAttempt>;

const fieldHeight = z
	.object({
		height_mm: z.number().int().min(1).max(FIELD_HEIGHT_MAX_MM),
		tries: z.string().regex(FIELD_TRIES_PATTERN),
	})
	.strict();
export type FieldBenchHeight = z.infer<typeof fieldHeight>;

export const fieldBenchAthlete = z
	.object({
		/** What OVR matches on: the slot's bib. */
		bib: z.string().regex(TIMING_LANE_PATTERN),
		/** The device's competition order, shown only. */
		order: z.number().int().min(1).max(999),
		status: z.enum(FIELD_ATHLETE_STATUSES),
		/** The device's place — only compared with the rank OVR computes, never stored. */
		place: z.number().int().min(1).max(RESULT_RANK_MAX).nullable(),
		attempts: z.array(fieldAttempt).max(FIELD_ATTEMPTS_MAX),
		heights: z.array(fieldHeight).max(FIELD_HEIGHTS_MAX),
	})
	.strict();
export type FieldBenchAthlete = z.infer<typeof fieldBenchAthlete>;

/** One field console's latest card, snake_case on the wire. */
export const fieldBenchSnapshot = z
	.object({
		kind: z.literal("FIELD"),
		system: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
		device_id: z.string().trim().min(1).max(64).optional(),
		simulated: z.boolean(),
		captured_at: z.string().datetime({ offset: true }),
		warnings: z.array(z.string().max(200)).max(20),
		/** The competition, in the device's words ("Event 101 · Men's Long Jump · Final"). */
		race_label: z.string().trim().max(120).optional(),
		/** The meet the device says it is on (the simulator is told by TSR); null = it does not say. Apply refuses another meet. */
		meet_id: z.string().uuid().nullable(),
		event_kind: z.enum(FIELD_EVENT_KINDS),
		phase: z.enum(FIELD_BENCH_PHASES),
		/** The round being jumped / thrown (horizontal, throws); null when the device does not say. */
		round: z.number().int().min(1).max(FIELD_ATTEMPTS_MAX).nullable(),
		/** The bar now (vertical), mm. */
		bar_mm: z.number().int().min(1).max(FIELD_HEIGHT_MAX_MM).nullable(),
		/** Who is up. */
		up_bib: z.string().regex(TIMING_LANE_PATTERN).nullable(),
		athletes: z.array(fieldBenchAthlete).max(START_LIST_SLOTS_MAX * 2),
	})
	.strict();
export type FieldBenchSnapshot = z.infer<typeof fieldBenchSnapshot>;

/**
 * What `PUT /timing/benches/:benchId` takes: a lane bench (POOL / TRACK, kind
 * optional — absent is POOL) or a FIELD card. The lane schema's `kind` enum
 * leaves FIELD out, so the two never overlap.
 */
export const benchSnapshot = z.union([timingBenchSnapshot, fieldBenchSnapshot]);
export type BenchSnapshotInput = z.infer<typeof benchSnapshot>;

/** The rows a bench reports: lanes, or athletes. */
export const benchRowCount = (s: BenchSnapshotInput): number => (s.kind === "FIELD" ? s.athletes.length : s.lanes.length);

// ─── Which field event a meet is ─────────────────────────────────────────────

const VERTICAL_CODE = /(HJ|PV|HIGJUM|POLVAU)$/i;
const HORIZONTAL_CODE = /(LJ|TJ|LONJUM|TRIJUM)$/i;
const THROW_CODE = /(SP|DT|HT|JT|SHOPUT|DISTHR|HAMTHR|JAVTHR)$/i;

/** The vertical jumps by event code (high jump, pole vault) — OVR's heights board follows it. */
export const isVerticalJumpCode = (eventCode: string): boolean => VERTICAL_CODE.test(eventCode);
/** The horizontal jumps by event code (long, triple) — the ones with a wind reading. */
export const isHorizontalJumpCode = (eventCode: string): boolean => HORIZONTAL_CODE.test(eventCode);

const COMBINED_KIND: Readonly<Record<string, FieldEventKind>> = { HJ: "VERTICAL", PV: "VERTICAL", LJ: "HORIZONTAL", TJ: "HORIZONTAL", SP: "THROW", DT: "THROW", HT: "THROW", JT: "THROW" };

/**
 * A field meet's kind from its codes: a combined event's discipline, a
 * planned bar progression (vertical), else the event code. null = not known
 * (Apply then trusts the device's kind).
 */
export function fieldEventKindOf(eventCode: string, config: Record<string, unknown> | null | undefined): FieldEventKind | null {
	const combined = readCombined(config);
	if (combined) return COMBINED_KIND[combined.discipline] ?? null;
	if (Array.isArray(config?.heights) && config.heights.length > 0) return "VERTICAL";
	if (VERTICAL_CODE.test(eventCode)) return "VERTICAL";
	if (HORIZONTAL_CODE.test(eventCode)) return "HORIZONTAL";
	if (THROW_CODE.test(eventCode)) return "THROW";
	return null;
}

/** The simulator's discipline ids — the last part of `/api/athletics/field/<id>`. */
export const ATHLETICS_FIELD_DISCIPLINE_IDS = ["high", "pole", "long", "triple", "shot", "discus", "hammer", "javelin"] as const;
export type AthleticsFieldDisciplineId = (typeof ATHLETICS_FIELD_DISCIPLINE_IDS)[number];

const DISCIPLINE_BY_CODE: Readonly<Record<string, AthleticsFieldDisciplineId>> = {
	HJ: "high",
	PV: "pole",
	LJ: "long",
	TJ: "triple",
	SP: "shot",
	DT: "discus",
	HT: "hammer",
	JT: "javelin",
};

/** The programme's long codes (`MHIGJUM`, `WSHOPUT`, `WLONJUM`) — the same words `fieldEventKindOf` reads. */
const DISCIPLINE_BY_LONG_CODE: Readonly<Record<string, AthleticsFieldDisciplineId>> = {
	HIGJUM: "high",
	POLVAU: "pole",
	LONJUM: "long",
	TRIJUM: "triple",
	SHOPUT: "shot",
	DISTHR: "discus",
	HAMTHR: "hammer",
	JAVTHR: "javelin",
};

/**
 * Which simulator competition a meet reads: a combined event's discipline,
 * else the event code's ending — the programme's long form (`MHIGJUM`) or the
 * short form (`MHJ`). null = not a field event it knows.
 */
export function athleticsFieldDisciplineOf(eventCode: string, config: Record<string, unknown> | null | undefined): AthleticsFieldDisciplineId | null {
	const combined = readCombined(config);
	if (combined) return DISCIPLINE_BY_CODE[combined.discipline] ?? null;
	const code = eventCode.trim().toUpperCase();
	return DISCIPLINE_BY_LONG_CODE[code.slice(-6)] ?? DISCIPLINE_BY_CODE[code.slice(-2)] ?? null;
}

// ─── The simulator's card → a snapshot ───────────────────────────────────────

/** `GET /api/athletics/field/<discipline>` — only the fields read. Marks and heights are in metres. */
export interface AthleticsFieldFeed {
	simulated?: boolean | null;
	source?: string | null;
	discipline?: string | null;
	/** throw | horizontal | vertical */
	kind?: string | null;
	name?: string | null;
	eventNo?: number | string | null;
	round?: string | null;
	meetId?: string | null;
	/** ready | running | paused | finished */
	phase?: string | null;
	progress?: { round?: number | null; height?: number | null } | null;
	/** Vertical: `tN` → the bar height in metres ("2.00"). */
	attemptHeaders?: Record<string, string> | null;
	current?: { order?: number | null } | null;
	athletes?: AthleticsFieldAthlete[] | null;
}

export interface AthleticsFieldAthlete {
	order?: number | null;
	bib?: string | number | null;
	/** in | cut | out | retired | DNS */
	status?: string | null;
	place?: number | null;
	/** OVR's legacy card: `tN` → "7.47" / "X" / "-" (horizontal, throws) or the tries at that bar ("XO", vertical). */
	scores?: Record<string, string> | null;
	/** Horizontal / throws: each round's reading, with the wind. */
	attempts?: { round?: number | null; mark?: number | null; foul?: boolean | null; pass?: boolean | null; wind?: number | null }[] | null;
}

/** The bench system name: the simulator, or a real field console. */
export const ATHLETICS_FIELD_SYSTEMS = { simulated: "ATHLETICS_FIELD_SIM", live: "ATHLETICS_FIELD" } as const;

const KIND_WORD: Readonly<Record<string, FieldEventKind>> = { throw: "THROW", horizontal: "HORIZONTAL", vertical: "VERTICAL" };
const STATUS_WORD: Readonly<Record<string, FieldAthleteStatus>> = { in: "IN", cut: "CUT", out: "OUT", retired: "RETIRED", dns: "DNS" };
const PHASE_WORD: Readonly<Record<string, FieldBenchPhase>> = { ready: "READY", running: "RUNNING", paused: "PAUSED", finished: "FINISHED" };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const intIn = (v: unknown, min: number, max: number): number | null => (finite(v) && Number.isInteger(v) && v >= min && v <= max ? v : null);
const text = (v: unknown): string | null => (finite(v) ? String(v) : typeof v === "string" && v.trim() !== "" ? v.trim() : null);

/** Metres → mm, floored to the centimetre (World Athletics records field marks to the lower cm). */
export const fieldMarkMm = (metres: number): number => Math.floor(Math.round(metres * 1000) / 10) * 10;
/** Metres → mm, for a bar height (set to the centimetre already). */
const heightMm = (metres: number): number => Math.round(metres * 1000);

const tKeys = (scores: Record<string, string>) =>
	Object.keys(scores)
		.filter((k) => /^t\d+$/.test(k))
		.sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));

/**
 * The simulator's field card → one bench snapshot, kind FIELD. Pure.
 * `capturedAt` is when the relay read it (ISO 8601 with an offset).
 *
 * - **Kind** from the card's `kind` (throw / horizontal / vertical); a card
 *   without one is not a field competition → throws.
 * - **Horizontal / throws**: `attempts[]` per round — a mark (floored to the
 *   cm), a foul, a pass; a round with none of them is not jumped yet and is
 *   left out. Wind on horizontal jumps only, to one decimal.
 * - **Vertical**: `scores.tN` at `attemptHeaders.tN` — the tries must be a
 *   valid series (`FIELD_TRIES_PATTERN`), else that height is left out and
 *   warned.
 * - **Status** in / cut / out / retired / DNS; another word → IN, warned.
 * - An athlete without a usable bib is left out and warned: OVR matches on it.
 */
export function athleticsFieldToBenchSnapshot(feed: AthleticsFieldFeed, capturedAt: string): FieldBenchSnapshot {
	const eventKind = KIND_WORD[String(feed.kind ?? "").toLowerCase()];
	if (!eventKind) throw new Error(`not a field competition (kind "${feed.kind ?? ""}")`);
	const simulated = feed.simulated === true || feed.source === "simulator";
	const warnings: string[] = [];
	const warn = (w: string) => {
		if (warnings.length < 20) warnings.push(w.slice(0, 200));
	};

	const phaseWord = String(feed.phase ?? "").toLowerCase();
	const phase = PHASE_WORD[phaseWord] ?? "READY";
	if (phaseWord !== "" && !PHASE_WORD[phaseWord]) warn(`Phase "${feed.phase}" is not one OVR knows.`);

	const heightAt = new Map<string, number>();
	for (const [k, v] of Object.entries(feed.attemptHeaders ?? {})) {
		const m = Number(v);
		if (/^t\d+$/.test(k) && finite(m) && m > 0 && heightMm(m) <= FIELD_HEIGHT_MAX_MM) heightAt.set(k, heightMm(m));
	}

	const athletes: FieldBenchAthlete[] = [];
	const seen = new Set<string>();
	for (const a of feed.athletes ?? []) {
		const order = intIn(a?.order, 1, 999);
		const bib = text(a?.bib);
		if (order === null) continue;
		if (bib === null || !TIMING_LANE_PATTERN.test(bib)) {
			warn(`Athlete ${order}: no bib OVR can match (${bib ?? "none"}).`);
			continue;
		}
		if (seen.has(bib)) {
			warn(`Bib ${bib} is listed twice — both left out.`);
			const i = athletes.findIndex((x) => x.bib === bib);
			if (i >= 0) athletes.splice(i, 1);
			continue;
		}
		seen.add(bib);

		const statusWord = String(a.status ?? "").toLowerCase();
		const status = STATUS_WORD[statusWord] ?? "IN";
		if (statusWord !== "" && !STATUS_WORD[statusWord]) warn(`Bib ${bib}: status "${a.status}" is not one OVR knows.`);

		const attempts: FieldBenchAttempt[] = [];
		const heights: FieldBenchHeight[] = [];
		if (eventKind === "VERTICAL") {
			const scores = a.scores ?? {};
			for (const k of tKeys(scores)) {
				const tries = (scores[k] ?? "").trim().toUpperCase();
				if (tries === "") continue;
				const h = heightAt.get(k);
				if (h === undefined) warn(`Bib ${bib}: tries "${tries}" at ${k} with no bar height.`);
				else if (!FIELD_TRIES_PATTERN.test(tries)) warn(`Bib ${bib}: "${tries}" at ${formatDistance(h)} is not a series of tries.`);
				else heights.push({ height_mm: h, tries });
			}
			heights.sort((x, y) => x.height_mm - y.height_mm);
		} else {
			const byRound = new Map<number, FieldBenchAttempt>();
			for (const t of a.attempts ?? []) {
				const seq = intIn(t?.round, 1, FIELD_ATTEMPTS_MAX);
				if (seq === null) continue;
				const wind = eventKind === "HORIZONTAL" && finite(t.wind) && Math.abs(t.wind) <= FIELD_WIND_MAX_MPS ? Math.round(t.wind * 10) / 10 : null;
				if (t.foul === true) byRound.set(seq, { seq, result: "FOUL", mark_mm: null, wind_mps: wind });
				else if (t.pass === true) byRound.set(seq, { seq, result: "PASS", mark_mm: null, wind_mps: null });
				else if (finite(t.mark) && t.mark >= 0) {
					const mm = fieldMarkMm(t.mark);
					if (mm > FIELD_MARK_MAX_MM) warn(`Bib ${bib}: round ${seq} mark ${t.mark} m is out of range.`);
					else byRound.set(seq, { seq, result: "VALID", mark_mm: mm, wind_mps: wind });
				}
			}
			attempts.push(...[...byRound.values()].sort((x, y) => x.seq - y.seq));
		}

		athletes.push({ bib, order, status, place: intIn(a.place, 1, RESULT_RANK_MAX), attempts, heights });
	}
	athletes.sort((x, y) => x.order - y.order);

	const upOrder = intIn(feed.current?.order, 1, 999);
	const up = upOrder === null ? null : (athletes.find((x) => x.order === upOrder)?.bib ?? null);
	const bar = finite(feed.progress?.height) && feed.progress.height > 0 ? heightMm(feed.progress.height) : null;
	const label = [text(feed.eventNo) && `Event ${text(feed.eventNo)}`, text(feed.name), text(feed.round)].filter(Boolean).join(" · ");
	const meetId = typeof feed.meetId === "string" && UUID.test(feed.meetId) ? feed.meetId.toLowerCase() : null;

	return {
		kind: "FIELD",
		system: simulated ? ATHLETICS_FIELD_SYSTEMS.simulated : ATHLETICS_FIELD_SYSTEMS.live,
		simulated,
		captured_at: capturedAt,
		warnings,
		...(label ? { race_label: label.slice(0, 120) } : {}),
		meet_id: meetId,
		event_kind: eventKind,
		phase,
		round: eventKind === "VERTICAL" ? null : intIn(feed.progress?.round, 1, FIELD_ATTEMPTS_MAX),
		bar_mm: eventKind === "VERTICAL" && bar !== null && bar <= FIELD_HEIGHT_MAX_MM ? bar : null,
		up_bib: up,
		athletes,
	};
}

// ─── A snapshot → result rows for one ATTEMPTS meet ──────────────────────────

/** A device card as the ATTEMPTS detail stores it: attempts (with `best_seq`) or heights. */
export function fieldCardDetail(athlete: Pick<FieldBenchAthlete, "attempts" | "heights">, kind: FieldEventKind): { attempts?: Attempt[]; best_seq?: number; heights?: HeightTries[] } {
	if (kind === "VERTICAL") return athlete.heights.length > 0 ? { heights: athlete.heights.map((h) => ({ height: h.height_mm, tries: h.tries })) } : {};
	if (athlete.attempts.length === 0) return {};
	const attempts: Attempt[] = athlete.attempts.map((a) => {
		const out: Attempt = { seq: a.seq, result: a.result };
		if (a.result === "VALID" && a.mark_mm !== null) out.mark = a.mark_mm;
		if (a.wind_mps !== null && a.result !== "PASS") out.wind_mps = a.wind_mps;
		return out;
	});
	const best = attemptsSummary({ attempts }).bestSeq;
	return best === null ? { attempts } : { attempts, best_seq: best };
}

/** The IRM a device's card says, by the same rules as the grid: DNS, RET, or NM for an athlete done with no valid mark. null = none. */
export function fieldCardIrm(athlete: Pick<FieldBenchAthlete, "status" | "attempts" | "heights">, value: number | null, phase: FieldBenchPhase): IrmCode | null {
	if (athlete.status === "DNS") return athlete.attempts.length === 0 && athlete.heights.length === 0 ? "DNS" : null;
	if (athlete.status === "RETIRED") return "RET";
	const tried = athlete.attempts.length > 0 || athlete.heights.length > 0;
	const done = athlete.status === "OUT" || athlete.status === "CUT" || phase === "FINISHED";
	return value === null && tried && done ? "NM" : null;
}

export interface FieldBenchContext {
	meetId: string;
	/** The meet's kind (`fieldEventKindOf`); null = trust the device's. */
	eventKind: FieldEventKind | null;
	slots: readonly TimingSlot[];
	/** Slots whose result an operator wrote: never touched (MANUAL_OVERRIDE). */
	protect?: ReadonlySet<string>;
	/** A combined-event discipline: the points follow the result (`combinedPoints`, via the caller). */
	pointsOf?: (value: number | null, irm: IrmCode | null) => number | null;
}

/** One row for the save statement — `TimingRow` plus `points` for a combined event. */
export interface FieldBenchRow {
	meet_competitor_id: string;
	version: number;
	irm?: string | null;
	result_value?: number | null;
	result_display?: string | null;
	detail?: Record<string, unknown>;
	rank?: number | null;
	points?: number | null;
}

export interface FieldBenchOutcome {
	rows: FieldBenchRow[];
	problems: TimingProblem[];
}

/** JSON with object keys sorted, so a detail read back from `jsonb` (which orders keys its own way) compares equal to the one written. */
function canonicalJson(v: unknown): string {
	if (Array.isArray(v)) return `[${v.map(canonicalJson).join(",")}]`;
	if (v !== null && typeof v === "object") {
		const entries = Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined);
		entries.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
		return `{${entries.map(([k, x]) => `${JSON.stringify(k)}:${canonicalJson(x)}`).join(",")}}`;
	}
	return JSON.stringify(v ?? null);
}
const sameJson = (a: unknown, b: unknown) => canonicalJson(a ?? {}) === canonicalJson(b ?? {});

/** The card part of a detail (`attempts`, `best_seq`, `heights`), canonical — what an Apply compares. */
const cardPart = (detail: Record<string, unknown> | null | undefined) => canonicalJson({ attempts: detail?.attempts ?? null, best_seq: detail?.best_seq ?? null, heights: detail?.heights ?? null });

/** The device's card differs from what the slot holds: an Apply would write it. */
export function fieldCardDiffers(athlete: Pick<FieldBenchAthlete, "attempts" | "heights">, kind: FieldEventKind, detail: Record<string, unknown> | null | undefined): boolean {
	return cardPart(fieldCardDetail(athlete, kind)) !== cardPart(detail);
}

/** The detail with the device's card in place of the stored one (other keys kept). */
function withCard(stored: Record<string, unknown> | null | undefined, card: ReturnType<typeof fieldCardDetail>): Record<string, unknown> {
	const { attempts: _a, best_seq: _b, heights: _h, ...rest } = stored ?? {};
	return { ...rest, ...card };
}

const summaryOf = (detail: Record<string, unknown> | null | undefined) => attemptsSummary(detail as AttemptsResultDetail | null | undefined);

/**
 * A FIELD snapshot → the rows to save on one ATTEMPTS meet, and what was not
 * applied. Pure. The timing feed's rules, for a card:
 *
 * - **Another meet or another kind of event → nothing** (`RACE_MISMATCH`):
 *   the device names a meet that is not this one, or its event kind is not
 *   the meet's (a throws console on a long jump).
 * - **By bib**: no slot with it `UNKNOWN_LANE`, two slots `AMBIGUOUS_LANE`,
 *   listed twice `DUPLICATE_LANE`, an empty slot `EMPTY_SLOT`, a result an
 *   operator typed `MANUAL_OVERRIDE` — those athletes are skipped whole.
 * - **The card is the device's whole card**: its attempts (or heights)
 *   replace the slot's; the best mark, `best_seq` and the board text are
 *   computed (contracts' `attemptsSummary`, `formatDistance` — the grid's own).
 * - **The feed fills, it never decides an IRM**: it sets DNS / RET / NM on a
 *   slot that has none, never clears or replaces one (`IRM_DIFFERS`).
 * - **Ranks are ours**: every filled slot by best mark and countback
 *   (`rankByValue`), the device's cards in place; the device's place is only
 *   compared (`PLACE_MISMATCH`). The device stops non-finalists at the cut, so
 *   its cards hold only the trials that count.
 * - **Unchanged values write nothing.**
 */
export function normalizeFieldBench(snapshot: FieldBenchSnapshot, ctx: FieldBenchContext): FieldBenchOutcome {
	const problems: TimingProblem[] = [];
	const report = (code: TimingProblemCode, lane: string | null, slotId: string | null = null) => problems.push({ code, lane, meet_competitor_id: slotId });

	if ((snapshot.meet_id !== null && snapshot.meet_id !== ctx.meetId.toLowerCase()) || (ctx.eventKind !== null && ctx.eventKind !== snapshot.event_kind)) {
		report("RACE_MISMATCH", null);
		return { rows: [], problems };
	}
	const kind = snapshot.event_kind;

	const byBib = new Map<string, TimingSlot[]>();
	for (const slot of ctx.slots) {
		const k = timingLaneKey(slot.bib);
		if (k === null) continue;
		byBib.set(k, [...(byBib.get(k) ?? []), slot]);
	}
	const counts = new Map<string, number>();
	for (const a of snapshot.athletes) counts.set(timingLaneKey(a.bib) ?? a.bib, (counts.get(timingLaneKey(a.bib) ?? a.bib) ?? 0) + 1);

	interface Write {
		detail: Record<string, unknown>;
		value: number | null;
		irm: IrmCode | null;
		display: string | null;
	}
	const writes = new Map<string, Write>();
	const places = new Map<string, { place: number; bib: string }>();
	const reported = new Set<string>();

	for (const a of snapshot.athletes) {
		const key = timingLaneKey(a.bib) ?? a.bib;
		if ((counts.get(key) ?? 0) > 1) {
			if (!reported.has(key)) report("DUPLICATE_LANE", a.bib);
			reported.add(key);
			continue;
		}
		const matches = byBib.get(key) ?? [];
		if (matches.length === 0) {
			report("UNKNOWN_LANE", a.bib);
			continue;
		}
		if (matches.length > 1) {
			report("AMBIGUOUS_LANE", a.bib);
			continue;
		}
		const slot = matches[0]!;
		if (slot.competitor === null) {
			report("EMPTY_SLOT", a.bib, slot.meet_competitor_id);
			continue;
		}
		if (ctx.protect?.has(slot.meet_competitor_id)) {
			report("MANUAL_OVERRIDE", a.bib, slot.meet_competitor_id);
			continue;
		}

		const card = fieldCardDetail(a, kind);
		const detail = withCard(slot.detail, card);
		const value = summaryOf(detail).value;
		const deviceIrm = fieldCardIrm(a, value, snapshot.phase);
		let irm: IrmCode | null = slot.irm;
		if (slot.irm === null) irm = deviceIrm;
		else if (deviceIrm !== slot.irm) report("IRM_DIFFERS", a.bib, slot.meet_competitor_id);
		const display = irm ?? (value === null ? null : formatDistance(value));
		writes.set(slot.meet_competitor_id, { detail, value, irm, display });
		if (a.place !== null) places.set(slot.meet_competitor_id, { place: a.place, bib: a.bib });
	}

	const filled = ctx.slots.filter((s) => s.competitor !== null);
	const ranks = rankByValue(
		filled.map((s) => {
			const w = writes.get(s.meet_competitor_id);
			const summary = summaryOf(w ? w.detail : s.detail);
			return { id: s.meet_competitor_id, value: w ? w.value : s.result_value, irm: w ? w.irm : s.irm, tieBreak: summary.tieBreak };
		}),
		false,
	);
	for (const [slotId, { place, bib }] of places) if (ranks.get(slotId) !== place) report("PLACE_MISMATCH", bib, slotId);

	const rows: FieldBenchRow[] = [];
	for (const slot of filled) {
		const w = writes.get(slot.meet_competitor_id);
		const rank = ranks.get(slot.meet_competitor_id) ?? null;
		const row: FieldBenchRow = { meet_competitor_id: slot.meet_competitor_id, version: slot.version };
		if (w) {
			if (w.irm !== slot.irm) row.irm = w.irm;
			if (w.value !== slot.result_value) row.result_value = w.value;
			if (w.display !== slot.result_display) row.result_display = w.display;
			if (!sameJson(w.detail, slot.detail)) row.detail = w.detail;
			if (ctx.pointsOf && (row.irm !== undefined || row.result_value !== undefined)) row.points = ctx.pointsOf(w.value, w.irm);
		}
		if (rank !== slot.rank) row.rank = rank;
		if (Object.keys(row).length > 2) rows.push(row);
	}
	return { rows, problems };
}
